import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';
import { Drawers } from 'app/types/drawer';
import { DataForm, FieldProps, FieldType, FileType } from 'components';
import { WorkerDocument } from 'app/types/worker';
import { useGetDocumentTypesQuery } from 'app/services/workers';
import { API_DATE_FORMAT, formats } from 'utils/datetime';
import { normalizeFile } from 'utils/file';
import { documentTypeName } from 'utils/worker';

export const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024;

export const toApiDate = (value?: Moment) => (value ? value.format(API_DATE_FORMAT) : undefined);

type DocumentFormProps = {
  handleSubmit: (values: any) => void;
  meta: any;
  unitId: number;
  document?: WorkerDocument;
};

const DocumentForm = ({ document, unitId, handleSubmit, meta }: DocumentFormProps) => {
  const { t } = useTranslation();
  const { data: types = [], isLoading } = useGetDocumentTypesQuery(unitId);
  const typeOptions = types.map((type) => ({ Id: type.Id, Name: documentTypeName(type) }));

  const fields: FieldProps[] = [
    {
      name: 'DocumentTypeId',
      label: t('entities.workerdocument.documenttypeid'),
      type: FieldType.SELECT,
      required: true,
      selectProps: { options: typeOptions, labelKey: 'Name', valueKey: 'Id', allowClear: false, loading: isLoading },
    },
    {
      name: 'File',
      label: t('entities.workerdocument.file'),
      type: FieldType.FILE,
      allowedFileType: FileType.DOCUMENT,
      maxFileSize: MAX_DOCUMENT_SIZE,
    },
    {
      name: 'ValidFrom',
      label: t('entities.workerdocument.validfrom'),
      type: FieldType.DATE,
      datePicker: { dateFormat: formats.DD_MM_YYYY },
    },
    {
      name: 'ValidTo',
      label: t('entities.workerdocument.validto'),
      type: FieldType.DATE,
      datePicker: { dateFormat: formats.DD_MM_YYYY },
    },
  ];

  const initialValues = document ? {
    ...document,
    ValidFrom: document.ValidFrom ? moment(document.ValidFrom) : undefined,
    ValidTo: document.ValidTo ? moment(document.ValidTo) : undefined,
    File: normalizeFile(document.FileName ?? 'Document', document.FileUrl),
  } : {};

  return (
    <DataForm
      id={document ? Drawers.EDIT_WORKER_DOCUMENT : Drawers.ADD_WORKER_DOCUMENT}
      entity="workerdocument"
      onSubmit={handleSubmit}
      fields={fields}
      meta={meta}
      initialValues={initialValues}
    />
  );
};

export default DocumentForm;
